import {BusinessUnitEditApi} from '../../../api/businessUnitEditApi';
import {ERROR_AD_ACCOUNT_NOT_LINKED_TO_AGENCY_CLIENT} from '../../../settings/settings';

/**
 * Las tres escrituras de gestión de una línea de control: desactivar, reactivar y eliminar.
 *
 * Viven acá y no en un componente porque desde el 2026-09-10 tienen DOS puertas —el menú `⋮` de la
 * fila y la sección «Gestión» del `LineEditDrawer`— y la card IysOiDWB fijó la invariante de que
 * siga habiendo **un solo camino de escritura**. Dos copias de esta lógica divergen: el caso de
 * `needsLinkConfirm` es exactamente el tipo de rama que una de las dos se olvida de manejar.
 *
 * Son funciones y no un hook a propósito: `LineEditDrawer` es un class component y no puede
 * consumir hooks. Y devuelven un RESULTADO en vez de pintar la pantalla —nada de `toast` acá
 * adentro— porque cada superficie confirma distinto: el drawer con su confirm inline (que ocupa el
 * lugar del pie) y la fila con un `YesNoModal` del shell.
 *
 * Forma del resultado: `{ok, message, needsLinkConfirm}`.
 * - `ok: true`  → `message` es el texto de éxito, listo para el toast.
 * - `ok: false` → `message` es el motivo. El `error_message` del backend se propaga A PROPÓSITO:
 *   los 400 de estos endpoints son accionables y en español, y taparlos con un genérico ya mordió
 *   en el media plan (2026-07-31). Son endpoints scopeados por agencia — no hay nada cross-agency
 *   que filtrar.
 * - `needsLinkConfirm: true` → sólo al reactivar: el backend avisa que las cuentas de la línea no
 *   están vinculadas al cliente y pide permiso. No es un error: hay que volver a llamar con
 *   `addAccountsToClient: true`.
 */

// El genérico compartido por las tres. `t` se recibe por parámetro (estas funciones corren tanto
// desde un hook `useTranslation` como desde el `this.props.t` de un class component).
function fallbackMessage(t) {
    return t('client_panel.line_drawer_save_error', 'No pudimos guardar los cambios');
}

/**
 * Los DOS mensajes que estos endpoints emiten en inglés crudo, y qué decir en su lugar.
 *
 * El resto de lo que puede responder `change_bu_status` / `delete` / el linking de cuentas ya está
 * en español y es accionable, así que viaja tal cual (es la decisión que los dos reviews avalan).
 * Estos dos no: son estados imposibles, no cosas que el usuario pueda corregir —
 * `'There is no business unit with this ID'` es que la línea ya no existe (otra pestaña la borró) y
 * `'The requested action is invalid'` es un bug de programación. Traducirlos literal sería igual de
 * inútil: lo que hace falta es decir qué pasó.
 *
 * Se mapea por el texto EXACTO y no por un heurístico de «parece inglés»: si el backend algún día
 * los traduce, el mapa deja de matchear solo y sale su mensaje, que es lo que se quiere.
 * Origen: `core/commands/change_bu_status.py:38` y `:64` (backend).
 */
const MENSAJES_NO_ACCIONABLES = {
    'There is no business unit with this ID': ['client_panel.line_manage_gone',
        'Esta línea ya no existe. Puede haberla eliminado otra persona o pestaña.'],
    'The requested action is invalid': ['client_panel.line_drawer_save_error',
        'No pudimos guardar los cambios'],
};

function failureFrom(response, t) {
    const crudo = response && response.error_message;
    const mapeado = crudo && MENSAJES_NO_ACCIONABLES[crudo];
    return {
        ok: false,
        message: mapeado ? t(mapeado[0], mapeado[1]) : (crudo || fallbackMessage(t)),
        needsLinkConfirm: false,
    };
}

export function deactivateLine(lineId, t) {
    return BusinessUnitEditApi.deactivate(lineId).then((response) => {
        if (response && response.status === 'OK') {
            return {
                ok: true,
                message: t('client_panel.line_manage_deactivated', 'Línea desactivada'),
                needsLinkConfirm: false,
            };
        }
        return failureFrom(response, t);
    });
}

export function activateLine(lineId, {addAccountsToClient = false} = {}, t) {
    return BusinessUnitEditApi.activate(lineId, {addAccountsToClient}).then((response) => {
        if (response && response.status === 'OK') {
            return {
                ok: true,
                message: t('client_panel.line_manage_activated', 'Línea reactivada'),
                needsLinkConfirm: false,
            };
        }
        // El backend pide confirmación para vincular las cuentas al cliente (mismo flujo que el
        // modal de Monitoreo): no es un fallo, es una pregunta. Se reintenta con `true`.
        if (response && response.status === ERROR_AD_ACCOUNT_NOT_LINKED_TO_AGENCY_CLIENT) {
            return {ok: false, message: null, needsLinkConfirm: true};
        }
        return failureFrom(response, t);
    });
}

export function deleteLine(lineId, t) {
    return BusinessUnitEditApi.remove(lineId).then((response) => {
        if (response && response.status === 'OK') {
            return {
                ok: true,
                message: t('client_panel.line_manage_deleted', 'Línea eliminada'),
                needsLinkConfirm: false,
            };
        }
        return failureFrom(response, t);
    });
}
